import * as React from 'react'

import {
  AtsMessageTemplateHeaderDocument,
  AtsSettingsTabsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { ModuleMeta } from 'modules/core/lib/navigation/types'
import type { NavigationRoutesFn } from 'modules/core/lib/react-router'

const SettingsRootPage = React.lazy(() => import('modules/ats/pages/settings/RootPage'))

const AtsMessageTemplatePage = React.lazy(
  () =>
    import('modules/ats/pages/settings/TemplatesPage/sections/MessageTemplates/MessageTemplatePage')
)
const CandidateDataPage = React.lazy(() => import('modules/ats/pages/settings/CandidateDataPage'))
const CustomizationPage = React.lazy(() => import('modules/ats/pages/settings/CustomizationPage'))
const IntegrationsPage = React.lazy(() => import('modules/ats/pages/settings/IntegrationsPage'))
const ProcessesPage = React.lazy(() => import('modules/ats/pages/settings/ProcessesPage'))
const FactorialAIPage = React.lazy(() => import('modules/ats/pages/settings/FactorialAIPage'))
const TemplatesPage = React.lazy(() => import('modules/ats/pages/settings/TemplatesPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.ats.path,
      element: <SettingsRootPage />,
      handle: {
        resource: true,
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: AtsSettingsTabsDocument,
          }).map(({ data }): ModuleMeta => {
            const canManageAtsSettings = data?.ats._policies.manageAtsSettings.can ?? false
            const aiTopMatchAvailable = data?.companies.aiTopMatchFeature?.available ?? false

            const devQuestionTemplatesFeature =
              data?.companies.devQuestionTemplatesFeature?.available ?? false
            const templatesFeaturesAvailability = data?.companies.templatesFeatures.nodes
            const featureAvailable = (id: string) => {
              return templatesFeaturesAvailability.find((node) => node.id === id)?.available
            }
            const isQuestionTemplatesVisible =
              featureAvailable('ats_customized_features') && devQuestionTemplatesFeature
            const isMessageTemplatesVisible = featureAvailable('ats_message_templates')
            const isEvaluationFormsVisible =
              canManageAtsSettings && featureAvailable('ats_evaluation_forms')

            return {
              title: i18n.t('company_settings.people.recruitment.title'),
              subNavigation: [
                {
                  index: true,
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.customization'),
                  href: navigation.settings.ats.fullPath(),
                },
                {
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.templates'),
                  href: navigation.settings.ats.templates.fullPath(),
                  isVisible:
                    isQuestionTemplatesVisible ||
                    isMessageTemplatesVisible ||
                    isEvaluationFormsVisible,
                },
                {
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.processes'),
                  href: navigation.settings.ats.processes.fullPath(),
                  isVisible: canManageAtsSettings,
                },
                {
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.factorial_ai'),
                  href: navigation.settings.ats.factorialAI.fullPath(),
                  isVisible: canManageAtsSettings && aiTopMatchAvailable,
                },
                {
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.candidate_data'),
                  href: navigation.settings.ats.candidateData.fullPath(),
                  isVisible: canManageAtsSettings,
                },
                {
                  title: i18n.t('app_store_v2.apps.ats.settings.tabs.integrations'),
                  href: navigation.settings.ats.integrations.fullPath(),
                  isVisible: canManageAtsSettings,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <CustomizationPage />,
        },
        {
          path: navigation.settings.ats.templates.path,
          handle: {
            meta: ({}) => {
              return {
                title: i18n.t('app_store_v2.apps.ats.settings.tabs.templates'),
              }
            },
          },
          children: [
            {
              index: true,
              element: <TemplatesPage />,
            },
            {
              path: navigation.settings.ats.templates.messageTemplatesId.path,
              element: <AtsMessageTemplatePage />,
              handle: {
                resource: true,
                meta: ({ params: { messageTemplateId }, watchQuery }) => {
                  return watchQuery({
                    query: AtsMessageTemplateHeaderDocument,
                    variables: {
                      id: Number(messageTemplateId),
                    },
                  }).map(({ data }) => {
                    const title =
                      data?.ats.messageTemplate?.name ??
                      i18n.t('app_store_v2.apps.ats.settings.tabs.message_templates')

                    return {
                      title,
                      headerComponent: 'auto',
                    }
                  })
                },
              },
            },
          ],
        },
        {
          path: navigation.settings.ats.factorialAI.path,
          element: <FactorialAIPage />,
        },
        {
          path: navigation.settings.ats.processes.path,
          element: <ProcessesPage />,
        },
        {
          path: navigation.settings.ats.candidateData.path,
          element: <CandidateDataPage />,
        },
        {
          path: navigation.settings.ats.integrations.path,
          element: <IntegrationsPage />,
        },
      ],
    },
  ]
}

export default settingsRoutes
